import { Box, Button, Dialog, DialogActions, DialogTitle, Typography } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import imageCompression from "browser-image-compression";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { fetchAvailableQRCodes } from "../../api/qrCodes";
import {
  createPet,
  deletePetById,
  fetchPets as fetchPetsRequest,
  updatePet,
} from "../../api/pets";
import PetForm from "./components/PetForm";
import PetList from "./components/PetList";

const initialPet = {
  pet_code: "",
  name: "",
  species: "",
  contact_name: "",
  primary_phone: "",
  primary_lada: "+54",
  secondary_phone: "",
  secondary_lada: "+54",
  zone: "",
  notes: "",
  status: "",
  qr_code_id: "",
};

const countryCodes = [
  { code: "+54", label: "ARG" },
  { code: "+52", label: "MEX" },
  { code: "+1", label: "USA" },
  { code: "+57", label: "COL" },
  { code: "+34", label: "ESP" },
];

const stripKnownLada = (phone) => {
  const normalizedPhone = phone ?? "";
  const knownPrefix = countryCodes.find(({ code }) =>
    normalizedPhone.startsWith(code)
  );
  return {
    lada: knownPrefix?.code ?? "+54",
    phone: knownPrefix
      ? normalizedPhone.slice(knownPrefix.code.length)
      : normalizedPhone,
  };
};

const getAssignedQr = (pet) => pet?.qr_code ?? pet?.qr_codes?.[0] ?? null;

const getQrFilterLabel = (qrCode) => {
  const qrLabel = qrCode?.label ?? qrCode?.description ?? `QR #${qrCode?.id ?? ""}`;
  return qrCode?.url ? `${qrLabel} - ${qrCode.url}` : qrLabel;
};

const mergePetIntoList = (petList, nextPet) => {
  if (!nextPet?.id) {
    return petList;
  }

  const existingIndex = petList.findIndex((pet) => pet.id === nextPet.id);
  if (existingIndex === -1) {
    return [nextPet, ...petList];
  }

  return petList.map((pet) => (pet.id === nextPet.id ? nextPet : pet));
};

const mergeQrOptions = (qrCodes, ...selectedQrs) => {
  const merged = [...selectedQrs.filter(Boolean), ...qrCodes];

  return merged.filter(
    (qrCode, index, list) =>
      qrCode?.id && list.findIndex((item) => item.id === qrCode.id) === index
  );
};

export default function PetPanel() {
  const [pets, setPets] = useState([]);
  const [availableQRCodes, setAvailableQRCodes] = useState([]);
  const [form, setForm] = useState(initialPet);
  const [photoFile, setPhotoFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalErrors, setGeneralErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [qrCodeFilter, setQrCodeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [petToDelete, setPetToDelete] = useState(null);
  const [expandedPetId, setExpandedPetId] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const itemsPerPage = 9;

  const qrCodeFilterOptions = mergeQrOptions(
    availableQRCodes,
    ...pets.map((pet) => getAssignedQr(pet)).filter(Boolean)
  )
    .map((qrCode) => ({
      value: String(qrCode.id),
      label: getQrFilterLabel(qrCode),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filteredPets = pets.filter((pet) => {
    const matchesSearch = `${pet.name} ${pet.pet_code}`
      .toLowerCase()
      .includes(searchTerm);
    const matchesQrCode =
      !qrCodeFilter || String(getAssignedQr(pet)?.id ?? "") === qrCodeFilter;

    return matchesSearch && matchesQrCode;
  });
  const indexOfLastPet = currentPage * itemsPerPage;
  const indexOfFirstPet = indexOfLastPet - itemsPerPage;
  const currentPets = filteredPets.slice(indexOfFirstPet, indexOfLastPet);
  const totalPages = Math.ceil(filteredPets.length / itemsPerPage);
  const editingPet = pets.find((pet) => pet.id === editingId) ?? null;
  const formAvailableQRCodes = mergeQrOptions(
    availableQRCodes,
    getAssignedQr(editingPet)
  );

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [petList, qrCodeList] = await Promise.all([
          fetchPetsRequest(),
          fetchAvailableQRCodes(),
        ]);
        setPets(petList);
        setAvailableQRCodes(qrCodeList);
      } catch (error) {
        if (error.status === 404) {
          setPets([]);
          return;
        }

        console.error("Error al cargar datos de mascotas", error);
        enqueueSnackbar(
          error.message || "No se pudieron cargar las mascotas o los QRs disponibles.",
          {
            variant: "error",
          }
        );
      }
    };

    loadInitialData();
  }, [enqueueSnackbar]);

  const resetForm = () => {
    setForm(initialPet);
    setPhotoFile(null);
    setEditingId(null);
    setFieldErrors({});
    setGeneralErrors([]);
  };

  const refreshPets = async () => {
    try {
      const petList = await fetchPetsRequest();
      setPets(petList);
      return petList;
    } catch (error) {
      if (error.status === 404) {
        setPets([]);
        return [];
      }

      throw error;
    }
  };

  const refreshAvailableQRCodes = async () => {
    const qrCodeList = await fetchAvailableQRCodes();
    setAvailableQRCodes(qrCodeList);
    return qrCodeList;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]:
        name === "qr_code_id"
          ? value === "" || value === null || typeof value === "undefined"
            ? ""
            : Number(value)
          : value,
    });
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      });
      setPhotoFile(compressedFile);
    } catch (error) {
      console.error("Error al comprimir la imagen:", error);
      enqueueSnackbar("No se pudo comprimir la imagen. Intenta con otra.", {
        variant: "error",
      });
    }
  };

  const validate = () => {
    const nextFieldErrors = {};

    if (!editingId && (!form.pet_code || Number.isNaN(Number(form.pet_code)))) {
      nextFieldErrors.pet_code = "El codigo es obligatorio y debe ser numerico.";
    }
    if (!form.name.trim()) nextFieldErrors.name = "El nombre es obligatorio.";
    if (!form.species.trim()) nextFieldErrors.species = "La especie es obligatoria.";
    if (!form.contact_name.trim()) {
      nextFieldErrors.contact_name = "El nombre de contacto es obligatorio.";
    }
    if (!form.primary_phone.trim()) {
      nextFieldErrors.primary_phone = "El telefono primario es obligatorio.";
    }
    if (
      form.secondary_phone &&
      !/^\d+$/.test(form.secondary_phone.trim())
    ) {
      nextFieldErrors.secondary_phone = "El telefono secundario solo debe contener numeros.";
    }
    if (!/^\d+$/.test(form.primary_phone.trim())) {
      nextFieldErrors.primary_phone = "El telefono primario solo debe contener numeros.";
    }
    if (!form.status || !["en_casa", "perdido"].includes(form.status)) {
      nextFieldErrors.status = 'Selecciona un estado valido.';
    }
    if (!form.qr_code_id || Number.isNaN(Number(form.qr_code_id))) {
      nextFieldErrors.qr_code_id = "Selecciona un unico QR activo.";
    }

    if (photoFile) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(photoFile.type)) {
        nextFieldErrors.photo = "La foto debe ser JPG o PNG.";
      }
      if (photoFile.size > 5120 * 1024) {
        nextFieldErrors.photo = "La foto no debe exceder los 5 MB.";
      }
    }

    setFieldErrors(nextFieldErrors);
    setGeneralErrors([]);
    return Object.keys(nextFieldErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const wasEditing = Boolean(editingId);
      const editingPetId = editingId;
      const basePayload = Object.entries(form).reduce((payload, [key, value]) => {
        if (
          key === "primary_lada" ||
          key === "secondary_lada" ||
          key === "qr_code_id" ||
          (wasEditing && key === "pet_code")
        ) {
          return payload;
        }

        payload[key] = value;
        return payload;
      }, {});

      basePayload.primary_phone = `${form.primary_lada}${form.primary_phone}`;
      basePayload.secondary_phone = form.secondary_phone
        ? `${form.secondary_lada}${form.secondary_phone}`
        : "";

      const requestPayload = photoFile
        ? (() => {
            const formData = new FormData();

            Object.entries(basePayload).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append("photo", photoFile);

            return formData;
          })()
        : basePayload;

      let savedPet = null;

      if (wasEditing) {
        savedPet = await updatePet(editingPetId, requestPayload, form.qr_code_id);
      } else {
        savedPet = await createPet(requestPayload, form.qr_code_id);
      }

      if (savedPet?.id) {
        setPets((prev) => mergePetIntoList(prev, savedPet));
        if (editingPetId === savedPet.id) {
          setExpandedPetId(savedPet.id);
        }
      }

      await Promise.all([refreshPets(), refreshAvailableQRCodes()]);
      resetForm();
      enqueueSnackbar(
        wasEditing
          ? "Mascota actualizada correctamente."
          : "Mascota agregada correctamente.",
        { variant: "success" }
      );
    } catch (error) {
      if (error.errors) {
        const formattedErrors = Object.values(error.errors).flat();
        setGeneralErrors(formattedErrors);
        enqueueSnackbar("Hay errores de validacion en el formulario.", {
          variant: "warning",
        });
      } else if (error.status === 404) {
        setGeneralErrors(["La mascota que intentas modificar ya no existe."]);
        enqueueSnackbar("La mascota no existe o fue eliminada.", {
          variant: "warning",
        });
      } else {
        console.error("Error al guardar mascota", error);
        setGeneralErrors([error.message || "Ocurrio un error al guardar la mascota."]);
        enqueueSnackbar(error.message || "Ocurrio un error al guardar la mascota.", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pet) => {
    const { photo_url, ...rest } = pet;
    const primaryContact = stripKnownLada(pet.primary_phone);
    const secondaryContact = stripKnownLada(pet.secondary_phone);

    setForm({
      ...initialPet,
      ...rest,
      primary_lada: primaryContact.lada,
      primary_phone: primaryContact.phone,
      secondary_lada: secondaryContact.lada,
      secondary_phone: secondaryContact.phone,
      qr_code_id: getAssignedQr(pet)?.id ?? "",
    });
    setEditingId(pet.id);
    setPhotoFile(null);
    setFieldErrors({});
    setGeneralErrors([]);
  };

  const confirmDelete = (petId) => {
    setPetToDelete(petId);
    setConfirmOpen(true);
  };

  const deletePet = async () => {
    setLoading(true);
    try {
      await deletePetById(petToDelete);
      setConfirmOpen(false);
      setPetToDelete(null);
      await refreshPets();
      enqueueSnackbar("Mascota eliminada correctamente.", {
        variant: "success",
      });
    } catch (error) {
      console.error("Error al eliminar mascota", error);
      setConfirmOpen(false);
      setPetToDelete(null);
      if (error.status === 404) {
        setPets((prev) => prev.filter((pet) => pet.id !== petToDelete));
        enqueueSnackbar("La mascota ya no existe.", { variant: "warning" });
      } else {
        enqueueSnackbar(error.message || "Ocurrio un error al eliminar la mascota.", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        mb: 4,
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 4, sm: 6 },
        borderRadius: "24px",
        backgroundColor: "#fcffff",
        border: "3px solid #e0f2e9",
        boxShadow: "0 8px 32px rgba(27, 141, 75, 0.08)",
      }}
    >
      <Typography
        variant="h2"
        component="h1"
        sx={{
          fontSize: "2.5rem",
          fontFamily: "'Comic Relief', cursive",
          fontWeight: 700,
          color: "#1B8D4B",
          mb: 3,
        }}
      >
        Agregar Mascotas
      </Typography>

      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
        <Box flex={1} sx={{ minWidth: "300px" }}>
          <PetForm
            availableQRCodes={formAvailableQRCodes}
            countryCodes={countryCodes}
            editingId={editingId}
            fieldErrors={fieldErrors}
            form={form}
            generalErrors={generalErrors}
            onCancel={resetForm}
            onChange={handleChange}
            onFileChange={handleFileChange}
            onSubmit={handleSubmit}
            photoFile={photoFile}
          />
        </Box>

        <Box flex={1}>
          <PetList
            currentPage={currentPage}
            currentPets={currentPets}
            expandedPetId={expandedPetId}
            filteredPets={filteredPets}
            itemsPerPage={itemsPerPage}
            onDelete={confirmDelete}
            onEdit={handleEdit}
            onPageChange={(_, value) => setCurrentPage(value)}
            onQrCodeFilterChange={(e) => {
              setQrCodeFilter(e.target.value);
              setCurrentPage(1);
            }}
            onSearchChange={(e) => {
              setSearchTerm(e.target.value.toLowerCase());
              setCurrentPage(1);
            }}
            onToggleExpand={(id) =>
              setExpandedPetId((prev) => (prev === id ? null : id))
            }
            qrCodeFilter={qrCodeFilter}
            qrCodeFilterOptions={qrCodeFilterOptions}
            totalPages={totalPages}
          />
        </Box>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          Estas seguro de que deseas eliminar esta mascota?
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="primary"
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            Cancelar
          </Button>
          <Button
            onClick={deletePet}
            color="error"
            variant="contained"
            sx={{ "&:hover": { backgroundColor: "red" } }}
          >
            Confirmar eliminacion
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
